import { GridOptions } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { LinkedContactsDetailsComponent } from './linked-contacts-details/linked-contacts-details.component';
import { LinkedContactsDetailsModel, LinkedContactsRowModel } from './linked-contacts.model';
import { LinkedContactsService } from './linked-contacts.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-linked-contacts',
    templateUrl: './linked-contacts.component.html',
    styleUrls: ['./linked-contacts.component.scss']
})
export class LinkedContactsComponent implements OnInit {

    @ViewChild('linkedContactsWidgetGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public data: LinkedContactsRowModel[] = null; // A copy of data returned by loadData will be stored here.
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;
    // Ag-grid configuration:
    public gridOptions: GridOptions = null;

    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
    private stateSaveTimer: number;

    private detailsLoading = false;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: LinkedContactsService,
        private ngbModal: NgbModal,
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        // Bound values ("@Input()" above) are only valid in and after this function.
        // Show the loading spinner.  This will be unset in the loadData() function:
        this.moduleManager.showLoader = true;

        try {
            await Promise.all([
                this.cvLocalizationService.GetSection("Linked_Contacts"),
            ]);
        }
        finally {
            this.setupGrid();
        }

        // Module Configuration:
        this.moduleManager.setOptions({
            // name: '' // sets the title of the module (defaults to the database entry's ModuleName, if unset).
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: settings => {
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true,
            refreshType: null,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
            onExport: (format) => this.service.loadDataForExport(this.moduleKey, format),
        });

        this.moduleManager.setMaxDateRange(7);

        //#region Menu Items

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        //#endregion

        //#region States and Settings
        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });
        //#endregion
    }

    // Called by AngularJS when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            // this.agGrid?.api.onFilterChanged(); // Used with the External Filter.
            this.filterValue = this.filterValue.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    openModal(data: LinkedContactsDetailsModel, sourceContact: LinkedContactsRowModel) {
        const modalRef = this.ngbModal.open(LinkedContactsDetailsComponent, {size : "xl modal-dialog-centered"});
        const component: LinkedContactsDetailsComponent = modalRef.componentInstance;
        component.data = data;
        component.moduleKey = this.moduleKey;
        component.moduleManager = this.moduleManager;
        this.detailsLoading = false;
    };

    private async loadData(isAutoRefresh) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
            }
            // else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.

            this.moduleManager.showLoader = false;
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
        }
    }

    private async loadLinkedContactsModal(moduleKey: string, masterContactId: string, linkedContactId: string) {
        this.moduleManager.showLoader = true;
        try {
            const response = await this.service.loadDetails(moduleKey, masterContactId, linkedContactId);
            return response;
        }
        catch {
            return null;
        }
        finally {
            this.moduleManager.showLoader = false;
        }
    }

    /**
     * Sets up the grid options.  Should be called after localizations are loaded, because the column headers are localized.
     */
    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({ // Please use AgGridHelperService.  This way your grid options will inherit logic all modules should have.  Properties you define in this object will replace the ones from the factory.
            // ag-grid can more efficiently update data that is mostly unchanged.  Please read getRowNodeId()'s comment.
            components: {
                ClickableRenderer: this.agGridHelperService.createClickableGroupRenderer(
                    async (params: any) => {
                        if (!this.detailsLoading) {
                            try {
                                // const masterContactIdToPull = params.data.LinkedContactId === "" ?
                                //     params.data.MasterContactId : params.data.LinkedContactId;
                                const masterContactId = params.data.MasterContactId;
                                const linkedContactId = params.data.LinkedContactId;
                                const detailedData = await this.loadLinkedContactsModal(this.moduleKey, masterContactId, linkedContactId);
                                if (detailedData)
                                    this.openModal(detailedData, params.data);
                            }
                            finally {
                                this.detailsLoading = false;
                            }
                        };
                    }
                ),
            },
            columnDefs: [
                {
                    field: "MasterContactId", // This is the key name from object representing this row's data.  rowData is an array of objects.
                    colId: "MasterContactId", // Specifying a column ID is optional, but if you change the fields, saved state won't match later.
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Master_Contact_ID", "Master Contact ID"),
                    filter: "agTextColumnFilter",
                    cellRenderer: "ClickableRenderer",
                },
                {
                    field: "ContactId",
                    colId: "ContactId",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Contact_ID", "Contact ID"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "LinkedContactId",
                    colId: "LinkedContactId",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Linked_Contact_ID", "Linked Contact ID"),
                    filter: "agTextColumnFilter",
                    valueGetter: params => params.data.LinkedContactId.length > 0 ? params.data.LinkedContactId : ' - ',
                },
                {
                    field: "BU",
                    colId: "BU",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "BU", "BU"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "Agent",
                    colId: "Agent",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Agent", "Agent"),
                    filter: "agTextColumnFilter",
                    valueGetter: params => params.data.Agent.length > 0 ?
                        params.data.Agent : this.cvLocalizationService.getCachedText("Linked_Contacts", "Unassigned", "Unassigned"),
                },
                {
                    field: "Team",
                    colId: "Team",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Team", "Team"),
                    filter: "agTextColumnFilter",
                    valueGetter: params => params.data.Team.length > 0 ? params.data.Team : ' - ',
                },
                {
                    field: "Skill",
                    colId: "Skill",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Skill", "Skill"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "StartDate",
                    colId: "StartDate",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Start_Date", "Start Date"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "CompanyNumber",
                    colId: "CompanyNumber",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Company_Number", "Company #"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "CustomerNumber",
                    colId: "CustomerNumber",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Customer_Number", "Customer #"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "Type",
                    colId: "Type",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Type", "Type"),
                    filter: "agTextColumnFilter",
                },
            ],
            onGridReady: params => {
                if (this.data) {
                    params.api.setGridOption('rowData', this.data);
                }
                if (this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
                this.applyFilter();
                this.agGrid?.api.sizeColumnsToFit();
            },

            // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
            // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display.
            getRowId: ({ data: rowData }) => `${rowData.ContactId}_${rowData.StateIndex}`,

            // The filter can also be set with the setQuickFilter() api call on the grid.
            // isExternalFilterPresent: () => this.filterValue.length > 0, // External Filter refers to our search bar.  This function returns true when $ctrl.filterValue has content, false when otherwise.  False causes ag-grid to show all row values.
            // doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue), // This function actually uses the value of the search bar, and it compares it to the data of the row ag-grid is passing in (node).  Use agGridHelperFactory's unless you have good reason.
            onSortChanged: event => this.queueStateSave(),
            onColumnMoved: event => this.queueStateSave(),
            onColumnPinned: event => this.queueStateSave(),
            onColumnVisible: event => {
                this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions, 50);
                this.queueStateSave();
            },
            onFirstDataRendered: event => {
                this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions, 50);
            },
            onGridSizeChanged: event => {
                this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions, 50);
            }
        });
    }

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    }
}
